import React, { useCallback, useEffect, useId, useState } from 'react';
import ReactDOM from 'react-dom';
import { isAllowedMediaUrl } from './mediaUrl';

/**
 * Channel avatar with a branded fallback and a full-screen profile view.
 *
 * A real profile picture (from an allowed host) opens large when tapped, the
 * way a chat app shows a contact's photo: a dimmed backdrop over the whole
 * page, the picture shown whole, the channel name under it, Escape or a tap
 * outside to close. It is portalled into document.body on purpose: the old
 * lightbox rendered inside the 38px clipped circle in the header's stacking
 * context and never appeared properly.
 *
 * Channels without a picture show the Grasp mark on the gold tint, so the
 * default state carries the brand instead of looking unset.
 */
function ProfileView({ src, name, onClose }) {
  const titleId = useId();

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      className="avatar-view"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <button
        type="button"
        className="avatar-view-close"
        onClick={onClose}
        aria-label="Close"
        autoFocus
      >
        ×
      </button>
      <figure className="avatar-view-figure">
        <img src={src} alt={name || 'Channel'} className="avatar-view-image" />
        <figcaption id={titleId} className="avatar-view-name">
          {name}
        </figcaption>
      </figure>
    </div>,
    document.body
  );
}

function ChannelAvatar({ src, name = '', zoomable = false, className = '' }) {
  const usable = src && isAllowedMediaUrl(src) ? src : null;
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);

  if (usable) {
    if (zoomable) {
      return (
        <>
          <button
            type="button"
            className={`channel-avatar channel-avatar--button ${className}`}
            onClick={() => setOpen(true)}
            aria-label={name ? `View ${name} profile picture` : 'View profile picture'}
          >
            <img src={usable} alt="" />
          </button>
          {open ? <ProfileView src={usable} name={name} onClose={close} /> : null}
        </>
      );
    }
    return (
      <span className={`channel-avatar ${className}`}>
        <img src={usable} alt="" />
      </span>
    );
  }

  return (
    <span
      className={`channel-avatar channel-avatar--fallback ${className}`}
      aria-hidden="true"
    >
      <img src="/brand/grasp-mark.svg" alt="" />
    </span>
  );
}

export default ChannelAvatar;
